import { BookingFlowEmbeddedShell } from "components/booking-flow";
import BookingFlowConfirmationBody, {
    BookingFlowConfirmationCheckListItem,
} from "../booking-flow-confirmation-body";
import useAuthUser from "hooks/auth-user";
import useBookingFlowRedesign from "hooks/useBookingFlowRedesign";
import useLanguage from "hooks/language";
import { kebabCase } from "lodash";
import { DateTime } from "luxon";
import { useEffect } from "react";
import { useAsync } from "react-async-hook";
import { NotificationManager } from "react-notifications";
import { useHistory, useParams } from "react-router-dom";
import { DEFAULT_TIMEZONE } from "utils/constants";
import { createIcsFile } from "utils/generateIcsFile";
import {
    getEstimatedResultsDateForAppointment,
    getLocalizedAppointmentDateWithTime,
    ServiceType,
} from "utils/private-service/date";
import { SCREENING_FLOW } from "models/screening-flow";
import routes from "utils/routes";
import { setScreeningFlow } from "utils/session-storage";
import { ReactComponent as Checkmark } from "../../../../assets/images/check-green.svg";
import { ReactComponent as Clock } from "../../../../assets/images/clock-icon.svg";
import { ReactComponent as MoveToInbox } from "../../../../assets/images/move-to-inbox.svg";
import { ReactComponent as Pin } from "../../../../assets/images/pin-icon.svg";
import IconInfoBlock from "../../../../components/icon-info-block";
import screeningPageStyles from "../screening.module.css";
import styles from "./screening-appointment-confirmation.module.css";

export default function ScreeningAppointmentConfirmation(): JSX.Element | null {
    const { language, translations } = useLanguage();
    const [authUser] = useAuthUser();
    const isBookingFlowRedesign = useBookingFlowRedesign();

    const t = translations.privateServiceSection;

    const { id } = useParams<{ id: string }>();

    const { replace } = useHistory();
    const appointment = authUser?.appointments.find(
        (appointment) => String(appointment.id) === id
    );

    useEffect(() => {
        setScreeningFlow(SCREENING_FLOW.STI);
    }, []);

    useEffect(() => {
        if (!authUser) return;

        if (!appointment) {
            NotificationManager.error(
                translations.notification.unexpectedError
            );
            replace(routes.APPOINTMENTS);
        }
    }, [
        authUser,
        appointment,
        replace,
        translations.notification.unexpectedError,
    ]);

    if (!authUser || !appointment) {
        return null;
    }

    const formattedAppointmentDate = getLocalizedAppointmentDateWithTime(
        appointment,
        language
    );
    const serviceType = appointment.usesPrivateLab
        ? ServiceType.private
        : ServiceType.public;
    const labTypeLabel = appointment.usesPrivateLab
        ? ` ${t.confirmation.privateLab}`
        : "";
    const estimatedResultsDate = getEstimatedResultsDateForAppointment(
        appointment,
        serviceType,
        language
    );
    const icsFileName = kebabCase(t.confirmation.icsFileTitle) + ".ics";
    const { result: iCalendar } = useAsync(async () => {
        return await createIcsFile({
            eventLocation: appointment.clinic.name,
            eventDateTime: DateTime.fromISO(appointment.datetime, {
                zone: DEFAULT_TIMEZONE,
            }),
            fileName: icsFileName,
            title: t.confirmation.title,
        });
    }, [language]);

    const confirmationContent = (
        <div
            className={`container ${styles.privateServiceConfirmationSection} ${
                isBookingFlowRedesign
                    ? styles.privateServiceConfirmationSectionBookingFlow
                    : ""
            }`}
        >
            {isBookingFlowRedesign ? (
                <BookingFlowConfirmationBody
                    appointment={appointment}
                    showEstimatedResults
                    subtitle={t.confirmation.subtitle}
                    remindersTitle={t.confirmation.reminder.title}
                    reminderItems={
                        <>
                            <BookingFlowConfirmationCheckListItem>
                                <strong>
                                    {t.confirmation.reminder.ramqCard.main}
                                </strong>{" "}
                                {t.confirmation.reminder.ramqCard.note}
                            </BookingFlowConfirmationCheckListItem>
                            <BookingFlowConfirmationCheckListItem>
                                <strong>
                                    {t.confirmation.reminder.paymentMethod.main}
                                </strong>{" "}
                                {t.confirmation.reminder.paymentMethod.note}
                            </BookingFlowConfirmationCheckListItem>
                            <BookingFlowConfirmationCheckListItem>
                                <strong>
                                    {
                                        t.confirmation.reminder.cancellationInfo
                                            .main
                                    }
                                </strong>{" "}
                                {t.confirmation.reminder.cancellationInfo.note}
                            </BookingFlowConfirmationCheckListItem>
                        </>
                    }
                    googleCalendarLink={appointment.addToGoogleCalendarLink}
                    iCalendarUrl={iCalendar?.url}
                    icsFileName={icsFileName}
                />
            ) : (
                <>
                    <div>
                        <Checkmark
                            className={
                                styles.privateServiceConfirmationSection__icon
                            }
                        />
                        <h2
                            className={
                                styles.privateServiceConfirmationSection__title
                            }
                        >
                            {t.confirmation.title}
                        </h2>
                        <div
                            className={
                                styles.privateServiceConfirmationSection__subtitle
                            }
                        >
                            {t.confirmation.subtitle}
                        </div>
                    </div>
                    <IconInfoBlock Icon={Pin} value={appointment.clinic.name} />
                    <IconInfoBlock
                        Icon={Clock}
                        label={t.appointment}
                        value={formattedAppointmentDate}
                    />
                    <IconInfoBlock
                        Icon={MoveToInbox}
                        label={t.results}
                        value={`${estimatedResultsDate}${labTypeLabel}`}
                    />
                    <div
                        className={
                            styles["privateServiceConfirmationSection__actions"]
                        }
                    >
                        <a
                            className={
                                styles[
                                    "privateServiceConfirmationSection__actions-link"
                                ]
                            }
                            target="_blank"
                            rel="noreferrer"
                            href={appointment.addToGoogleCalendarLink}
                        >
                            {t.confirmation.googleCalendar}
                        </a>
                        {iCalendar && (
                            <a
                                className={
                                    styles[
                                        "privateServiceConfirmationSection__actions-link"
                                    ]
                                }
                                href={iCalendar.url}
                                download={icsFileName}
                                aria-label={`Download calendar file: ${icsFileName}`}
                            >
                                {t.confirmation.appleCalendar}
                            </a>
                        )}
                    </div>
                    <div
                        className={
                            styles["privateServiceConfirmationSection__note"]
                        }
                    >
                        <div
                            className={
                                styles[
                                    "privateServiceConfirmationSection__note-title"
                                ]
                            }
                        >
                            {" "}
                            {t.confirmation.reminder.title}
                        </div>
                        <ul
                            className={
                                styles[
                                    "privateServiceConfirmationSection__note-list"
                                ]
                            }
                        >
                            <li
                                className={
                                    styles[
                                        "privateServiceConfirmationSection__note-item"
                                    ]
                                }
                            >
                                <strong>
                                    {t.confirmation.reminder.ramqCard.main}
                                </strong>{" "}
                                {t.confirmation.reminder.ramqCard.note}
                            </li>
                            <li
                                className={
                                    styles[
                                        "privateServiceConfirmationSection__note-item"
                                    ]
                                }
                            >
                                <strong>
                                    {t.confirmation.reminder.paymentMethod.main}
                                </strong>{" "}
                                {t.confirmation.reminder.paymentMethod.note}
                            </li>
                            <li
                                className={
                                    styles[
                                        "privateServiceConfirmationSection__note-item"
                                    ]
                                }
                            >
                                <strong>
                                    {
                                        t.confirmation.reminder.cancellationInfo
                                            .main
                                    }
                                </strong>{" "}
                                {t.confirmation.reminder.cancellationInfo.note}
                            </li>
                        </ul>
                    </div>
                </>
            )}
        </div>
    );

    if (isBookingFlowRedesign) {
        return (
            <div className={screeningPageStyles.pageWrapperBookingFlow}>
                <BookingFlowEmbeddedShell
                    currentStep="confirmation"
                    skipConsultationStep={appointment.usesPrivateLab}
                    compactTopPadding
                >
                    {confirmationContent}
                </BookingFlowEmbeddedShell>
            </div>
        );
    }

    return confirmationContent;
}
